/**
 * RecipientSelect
 *
 * The To/Cc/Bcc field shared by the Send-via-Email modal and the Communication Log
 * email popover. In `all` scope each instance owns its own `useContactSearch`, so the
 * three fields on a surface search independently and a keystroke in Cc never
 * repopulates To. In `form` scope the caller supplies an already-resolved, fixed list
 * and filtering happens client-side — no index query is issued.
 *
 * Server-side search means antd must not re-filter: `filterOption: false` shows exactly
 * the options the index returned. Free-typed addresses are still accepted (`mode="tags"`);
 * antd retains a selected option's label, so a committed tag keeps showing the contact's
 * name while the underlying value stays the address.
 *
 * `tokenSeparators` deliberately excludes the space (SYD-2230): a space used to commit
 * a half-typed name as a tag.
 */

import { FC, ReactNode, useCallback, useState } from 'react';
import { Select } from 'antd';
import { useContactSearch, type ContactOption } from '@/use/useContactSearch';

const TOKEN_SEPARATORS = [',', ';'];

const matchesQuery = (query: string, option?: ContactOption) => {
    const search = query.trim().toLowerCase();
    if (!search) return true;
    return (
        (option?.label ?? '').toLowerCase().includes(search) ||
        (option?.value ?? '').toLowerCase().includes(search)
    );
};

interface RecipientSelectProps {
    ariaLabel: string;
    placeholder: string;
    /** `form` offers only `formOptions`; `all` searches the contact entity */
    scope?: 'form' | 'all';
    /** The form's own people, already resolved by the caller, for `form` scope */
    formOptions?: ContactOption[];
    /** Whether the caller is still resolving `formOptions` */
    formOptionsLoading?: boolean;
    disabled?: boolean;
    maxTagCount?: number | 'responsive';
    maxTagPlaceholder?: (omitted: unknown[]) => ReactNode;
    /** Injected by Form.Item for label association */
    id?: string;
    value?: string[];
    onChange?: (value: string[]) => void;
}

const RecipientSelect: FC<RecipientSelectProps> = ({
    ariaLabel,
    placeholder,
    scope = 'all',
    formOptions,
    formOptionsLoading = false,
    disabled = false,
    maxTagCount,
    maxTagPlaceholder,
    id,
    value,
    onChange,
}) => {
    const [searchValue, setSearchValue] = useState('');
    const { options, loading, search } = useContactSearch({ kind: 'email' });
    const isFormScope = scope === 'form';

    const handleSearch = useCallback(
        (nextQuery: string) => {
            setSearchValue(nextQuery);
            if (!isFormScope) search(nextQuery);
        },
        [isFormScope, search]
    );

    /**
     * Clears the typed text but deliberately not the query: antd recovers a committed
     * tag's label from the options of the render in which it was selected, so dropping
     * the results in the same batch would leave the tag showing a bare address.
     */
    const handleChange = useCallback(
        (nextValue: string[]) => {
            if (nextValue.length > (value?.length ?? 0)) {
                setSearchValue('');
            }
            onChange?.(nextValue);
        },
        [onChange, value?.length]
    );

    const notFoundContent = (isFormScope ? formOptionsLoading : loading)
        ? 'Searching contacts...'
        : isFormScope
          ? 'No contacts on this form — type an email address'
          : searchValue.trim()
            ? 'No matching contacts — type an email address'
            : 'Type to search contacts';

    return (
        <Select
            id={id}
            mode="tags"
            placeholder={placeholder}
            tokenSeparators={TOKEN_SEPARATORS}
            options={isFormScope ? formOptions : options}
            showSearch={{
                searchValue,
                onSearch: handleSearch,
                filterOption: isFormScope ? (input, option) => matchesQuery(input, option) : false,
            }}
            optionRender={(option) => option.data.detail ?? option.label}
            notFoundContent={notFoundContent}
            aria-label={ariaLabel}
            disabled={disabled}
            maxTagCount={maxTagCount}
            maxTagPlaceholder={maxTagPlaceholder}
            value={value}
            onChange={handleChange}
            variant="filled"
        />
    );
};

export default RecipientSelect;
